import React from "react";
import { X, Sparkles, RotateCw, Wand2, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ACTIONS = [
  { key: "continue", label: "Continue scene" },
  { key: "rewrite", label: "Rewrite" },
  { key: "tighten", label: "Tighten" },
  { key: "expand", label: "Expand" },
  { key: "increase_tension", label: "Increase tension" },
  { key: "natural_dialogue", label: "More natural dialogue" },
  { key: "generate_alternatives", label: "Generate 2 alternatives" },
];

export default function AIPanel({ open, onClose, projectId, sceneId, selectionText, onInsert }) {
  const { updateCredits } = useAuth();
  const [action, setAction] = React.useState("continue");
  const [tone, setTone] = React.useState("");
  const [context, setContext] = React.useState(selectionText || "");
  const [output, setOutput] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setContext(selectionText || "");
    setOutput("");
  }, [selectionText, open]);

  const run = async () => {
    if (!context.trim()) {
      toast.error("Select some screenplay text first.");
      return;
    }
    setBusy(true);
    setOutput("");
    try {
      const { data } = await api.post("/ai/writer", {
        action, context, project_id: projectId, scene_id: sceneId || null, tone: tone || null,
      });
      setOutput(data.output);
      updateCredits(data.credits);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <aside className="w-[380px] shrink-0 border-l border-[#1E1E1E] bg-[#0F0F10] flex flex-col h-full" data-testid="ai-panel">
      <div className="h-14 border-b border-[#1E1E1E] px-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles size={14} className="text-[#D4AF37]" />
          <span>AI Writer</span>
        </div>
        <button onClick={onClose} className="text-[#71717A] hover:text-[#FFFFF0]" data-testid="ai-panel-close"><X size={16} /></button>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        <div>
          <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-2">Context</div>
          <textarea
            rows={6}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Paste or select the excerpt to work on…"
            className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#E4E4E7] p-3 text-xs font-mono-editor placeholder-[#71717A]"
            data-testid="ai-panel-context"
          />
          <div className="text-[10px] text-[#71717A] mt-1">{context.length} chars</div>
        </div>

        <div>
          <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-2">Action</div>
          <div className="grid grid-cols-1 gap-1.5">
            {ACTIONS.map((a) => (
              <button key={a.key}
                onClick={() => setAction(a.key)}
                className={`text-left px-3 py-2 text-xs border transition-colors ${action === a.key ? "border-[#D4AF37] bg-[#141414] text-[#FFFFF0]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#A1A1AA]"}`}
                data-testid={`ai-action-${a.key}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-2">Tone (optional)</div>
          <input value={tone} onChange={(e) => setTone(e.target.value)}
            placeholder="e.g. sardonic, tender, clinical…"
            className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#E4E4E7] px-3 py-2 text-xs placeholder-[#71717A]"
            data-testid="ai-panel-tone" />
        </div>

        <button onClick={run} disabled={busy}
          className="w-full bg-[#D4AF37] text-[#0A0A0A] py-2.5 text-xs font-medium hover:bg-[#F59E0B] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="ai-panel-run">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Wand2 size={14} /> Run</>}
        </button>

        {output && (
          <div className="border border-[#27272A] bg-[#0A0A0A]">
            <div className="border-b border-[#27272A] px-3 py-2 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Suggestion</div>
              <div className="flex gap-2">
                <button onClick={run} className="text-[#71717A] hover:text-[#FFFFF0]" title="Regenerate" data-testid="ai-regenerate">
                  <RotateCw size={12} />
                </button>
                <button onClick={() => onInsert?.(output)}
                  className="text-[#D4AF37] hover:text-[#F59E0B] inline-flex items-center gap-1 text-[10px] uppercase tracking-widest"
                  data-testid="ai-accept">
                  <Check size={12} /> Insert
                </button>
              </div>
            </div>
            <pre className="p-3 text-xs font-mono-editor whitespace-pre-wrap leading-relaxed text-[#E4E4E7]" data-testid="ai-output">{output}</pre>
          </div>
        )}
      </div>
    </aside>
  );
}
